"use client";
import React, { createContext, useState, useEffect } from "react";
import { BASE_URL, UPLOAD_URL } from "../config/config";

// Create the context
export const OrdersContext = createContext();

// Provider
export const OrdersProvider = ({ children }) => {
  const [ongoingOrders, setOngoingOrders] = useState([]);
  const [deliveredOrders, setDeliveredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrders = async () => {
    const userId = localStorage.getItem("user_id");
    const token = localStorage.getItem("token");

    if (!userId || !token) {
      setError("Missing user credentials. Please log in.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/orders/${userId}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        setOngoingOrders([]);
        setDeliveredOrders([]);
        setError(null);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      const result = await response.json();

      if (result.status === 200 && Array.isArray(result.data)) {
        const rawOrders = result.data;

        const ongoing = [];
        const delivered = [];

        rawOrders.forEach((order) => {
          const formattedOrder = {
            id: `#ORD${order.order_id}`,
            items: order.products.map((product) => ({
              name: product.product_name,
              quantity: Number(product.quantity),
              image: `${UPLOAD_URL}/products/${product.image}`,
            })),
            status: capitalize(order.order_status),
            orderDate: `Ordered on ${formatDateTime(order.create_audit_time)}`,
          };

          if (order.order_status === "delivered") {
            formattedOrder.deliveredDate = formatDate(order.delivered_audit_time);
            delivered.push(formattedOrder);
          } else if (order.order_status === "returned") {
            formattedOrder.returnedDate = formatDate(order.returnedDate || order.delivered_audit_time);
            delivered.push(formattedOrder);
          } else {
            ongoing.push(formattedOrder);
          }
        });

        setOngoingOrders(ongoing);
        setDeliveredOrders(delivered);
        setError(null);
      } else {
        throw new Error("Invalid API response.");
      }
    } catch (err) {
      console.error("Fetch Orders Error:", err);
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delay fetch until localStorage values are ready
  useEffect(() => {
    const interval = setInterval(() => {
      const userId = localStorage.getItem("user_id");
      const token = localStorage.getItem("token");

      if (userId && token) {
        clearInterval(interval);
        fetchOrders();
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const date = new Date(dateTimeStr);
    return date.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const date = new Date(dateTimeStr);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <OrdersContext.Provider
      value={{
        ongoingOrders,
        deliveredOrders,
        loading,
        error,
        refreshOrders: fetchOrders,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
};
